import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, ArrowLeft, Loader2, Mail, Stethoscope } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { supabase } from '@/lib/supabase/client';
import { useTranslation } from 'react-i18next';

export default function ForgotPasswordPage() {
  const { t } = useTranslation('doctor-platform');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use Supabase's built-in password reset
      // Since we have multiple apps, we need to handle the redirect properly
      // The Site URL in Supabase is set to retail.sahelianhealth.com
      // So we pass the full URL including the current app's domain
      const redirectUrl = `${window.location.origin}/auth/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: t('auth.forgotPassword.toast.title'),
        description: t('auth.forgotPassword.toast.description'),
      });
    } catch (error: any) {
      setError(error.message || t('auth.forgotPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 sm:p-12 lg:p-16 bg-white dark:bg-gray-900">
        <div className="mx-auto w-full max-w-md">
          <Button
            variant="ghost"
            className="mb-8"
            onClick={() => navigate('/auth/login')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('auth.forgotPassword.backToLogin')}
          </Button>

          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Stethoscope className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              {t('auth.forgotPassword.title')}
            </h1>
            <p className="text-muted-foreground">
              {t('auth.forgotPassword.subtitle')}
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isSubmitted ? (
            <div className="text-center space-y-6">
              <div className="bg-primary/10 text-primary p-4 rounded-lg">
                <Mail className="h-8 w-8 mx-auto mb-3 text-primary" />
                <h3 className="font-semibold mb-2">{t('auth.forgotPassword.submittedTitle')}</h3>
                <p className="text-sm">
                  {t('auth.forgotPassword.submittedDescription')}
                </p>
                <p className="text-sm font-medium mt-1">{email}</p>
              </div>
              <div className="text-sm text-muted-foreground">
                <p>{t('auth.forgotPassword.missingEmailPrompt')}</p>
                <p>{t('auth.forgotPassword.missingEmailHelp')}</p>
              </div>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                  }}
                >
                  {t('auth.forgotPassword.tryAnotherEmail')}
                </Button>
                <Link to="/auth/login" className="block">
                  <Button variant="ghost" className="w-full">
                    {t('auth.forgotPassword.backToLogin')}
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.forgotPassword.emailLabel')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.forgotPassword.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    required
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('auth.forgotPassword.emailHelp')}
                </p>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !email}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.forgotPassword.submitting')}
                  </>
                ) : (
                  t('auth.forgotPassword.submit')
                )}
              </Button>

              <div className="text-center text-sm">
                <span className="text-muted-foreground">
                  {t('auth.forgotPassword.rememberPrompt')}{' '}
                </span>
                <Link 
                  to="/auth/login" 
                  className="text-primary hover:underline font-medium"
                >
                  {t('auth.forgotPassword.signIn')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Right side - Medical themed gradient */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-500 to-teal-400">
          <div className="absolute inset-0 bg-black/20" />
        </div>
        <div className="relative h-full flex items-center justify-center p-16 text-white">
          <div className="max-w-xl text-center">
            <Stethoscope className="h-20 w-20 mx-auto mb-8 opacity-90" />
            <h2 className="text-4xl font-bold mb-6">
              {t('auth.forgotPassword.heroTitle')}
            </h2>
            <p className="text-xl opacity-90 leading-relaxed">
              {t('auth.forgotPassword.heroBody')}
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-white rounded-full mr-2" />
                <span className="text-sm opacity-75">{t('auth.forgotPassword.compliance')}</span>
              </div>
              <div className="flex items-center">
                <div className="h-2 w-2 bg-white rounded-full mr-2" />
                <span className="text-sm opacity-75">{t('auth.forgotPassword.encryptedData')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
